/* global document */
/*
 * Mobile-web "Happy App – Smart Business" download bar.
 *
 * An Apple-style Smart App Banner look-alike pinned to the top of the page for
 * every phone browser that does NOT get Apple's native banner:
 *   - Android (any browser)  → Google Play (org.happysolutions.app)
 *   - iOS non-Safari browsers (Chrome, Firefox, Facebook/Instagram in-app, …)
 *                            → App Store (id6782328664)
 * iOS Safari is skipped on purpose: it already renders the native banner from
 * the `apple-itunes-app` meta tag in web/index.html, two banners would stack.
 *
 * The CTA reads "Get" (store link) unless a previous hand-off proved the app is
 * installed, then "Open" (launches the app via the caller's `onOpen`).
 * Plain DOM + inline styles, dependency-free, so the same file lives in
 * admin (`src/pwa/appDownloadBanner.js`) — keep the
 * two copies in sync.
 */

export const IOS_APP_ID = '6782328664';
export const IOS_STORE_URL = `https://apps.apple.com/hu/app/happy-app-smart-business/id${IOS_APP_ID}`;
export const ANDROID_PACKAGE = 'org.happysolutions.app';
export const ANDROID_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;
export const APP_DOWNLOAD_BANNER_ID = 'happy-app-download-banner';

const APP_NAME = 'Happy App – Smart Business';
const PUBLISHER = 'Happy Solutions Kft.';
const BRAND_COLOR = '#F97316';

// Apple's Smart App Banner only renders in Safari itself — every other iOS
// browser / in-app WebView (all WebKit, so they also say "Safari/") needs ours.
const NON_SAFARI_IOS_RE = /(CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|GSA\/|YaBrowser|DuckDuckGo|FBAN|FBAV|Instagram|Line\/|MicroMessenger|Snapchat|musical_ly|TikTok)/i;

/**
 * Mobile OS of a user agent.
 * @param {string} userAgent
 * @returns {'ios'|'android'|null} `null` on desktop.
 */
export const getMobilePlatform = (userAgent = '') => {
    if (/iphone|ipad|ipod/i.test(userAgent)) {
        return 'ios';
    }

    return /android/i.test(userAgent) ? 'android' : null;
};

/**
 * Whether the browser is iOS Safari proper (→ Apple's native banner shows).
 * @param {string} userAgent
 * @returns {boolean}
 */
export const isIOSSafari = (userAgent = '') => getMobilePlatform(userAgent) === 'ios'
    && /Safari\//.test(userAgent)
    && !NON_SAFARI_IOS_RE.test(userAgent);

/**
 * Store URL for a platform. Google Play links carry an install referrer so
 * installs from the web banner show up as their own source in Play Console.
 * @param {'ios'|'android'} platform
 * @param {string} [source='web'] utm_source, e.g. `admin_web`
 * @returns {string}
 */
export const buildStoreUrl = (platform, source = 'web') => {
    if (platform === 'ios') {
        return IOS_STORE_URL;
    }
    const referrer = `utm_source=${source}&utm_medium=smart_banner&utm_campaign=web_to_app`;

    return `${ANDROID_STORE_URL}&referrer=${encodeURIComponent(referrer)}`;
};

const escapeHtml = value => String(value).replace(/[&<>"']/g, ch => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
}[ch]));

/**
 * Fires a GA4 event when the page has gtag (admin does; a no-op elsewhere).
 * @param {string} action view | get | open | dismiss
 * @param {string} platform
 * @returns {void}
 */
const trackBannerEvent = (action, platform) => {
    try {
        if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
            window.gtag('event', `app_banner_${action}`, { platform });
        }
    } catch (_error) {
        // analytics is best-effort only
    }
};

/**
 * Pin the download bar to the top of the page (once per page).
 *
 * @param {Object} options
 * @param {'ios'|'android'} options.platform Which store to link.
 * @param {(key: string, fallback: string) => string} [options.translate] i18n lookup (fallback = Hungarian).
 * @param {{ storeIos: string, storeAndroid: string, get: string, open: string, dismiss: string }} options.keys
 *        Translation keys of the host app.
 * @param {string} [options.iconUrl] App icon (square PNG).
 * @param {string} [options.source] utm_source of the Play install referrer.
 * @param {boolean} [options.appInstalled] A previous hand-off proved the app is installed → "Open".
 * @param {Function} [options.onOpen] Launches the installed app (used when `appInstalled`).
 * @param {Function} [options.onDismiss] Called after ✕ (e.g. to start the "stay on web" cooldown).
 * @returns {HTMLElement|null} The bar, or `null` when not shown.
 */
export const showAppDownloadBanner = ({
    platform,
    translate,
    keys,
    iconUrl,
    source,
    appInstalled = false,
    onOpen,
    onDismiss
} = {}) => {
    if (typeof document === 'undefined' || !document.body || !platform || document.getElementById(APP_DOWNLOAD_BANNER_ID)) {
        return null;
    }

    const t = (key, fallback) => {
        try {
            const value = translate && key ? translate(key, fallback) : fallback;

            return value && value !== key ? value : fallback;
        } catch (_error) {
            return fallback;
        }
    };
    const canOpen = appInstalled && typeof onOpen === 'function';
    const storeLine = platform === 'ios'
        ? t(keys?.storeIos, 'App Store-ból letölthető')
        : t(keys?.storeAndroid, 'Google Play-en elérhető');
    const ctaLabel = canOpen ? t(keys?.open, 'Megnyitás') : t(keys?.get, 'Letöltés');
    const dismissLabel = t(keys?.dismiss, 'Elvetés');
    const storeUrl = buildStoreUrl(platform, source);

    const bar = document.createElement('div');

    bar.id = APP_DOWNLOAD_BANNER_ID;
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', APP_NAME);
    bar.style.cssText = [
        'position:fixed', 'top:0', 'left:0', 'right:0', 'z-index:2147483000',
        'display:flex', 'align-items:center', 'gap:10px',
        'padding:calc(8px + env(safe-area-inset-top, 0px)) 12px 8px 6px',
        'background:#f9fafb', 'color:#111827', 'border-bottom:1px solid #e5e7eb',
        'box-shadow:0 2px 10px rgba(15,23,42,.08)',
        'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif'
    ].join(';');
    bar.innerHTML = `
<button type="button" data-happy-app-banner="dismiss" aria-label="${escapeHtml(dismissLabel)}" style="flex:0 0 auto;border:0;background:transparent;color:#9ca3af;font-size:22px;line-height:1;padding:6px;cursor:pointer">&times;</button>
${iconUrl ? `<img src="${escapeHtml(iconUrl)}" alt="" width="48" height="48" style="flex:0 0 48px;width:48px;height:48px;border-radius:11px;border:1px solid #e5e7eb;background:#fff;object-fit:cover">` : ''}
<div style="flex:1;min-width:0;line-height:1.25">
    <div style="font-weight:700;font-size:14px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${escapeHtml(APP_NAME)}</div>
    <div style="font-size:12px;color:#6b7280;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${escapeHtml(PUBLISHER)}</div>
    <div style="font-size:12px;color:#6b7280;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${escapeHtml(storeLine)}</div>
</div>
<a href="${escapeHtml(storeUrl)}" data-happy-app-banner="cta" target="_blank" rel="noopener noreferrer" style="flex:0 0 auto;background:${BRAND_COLOR};color:#fff;font-weight:700;font-size:14px;padding:7px 16px;border-radius:999px;text-decoration:none;white-space:nowrap">${escapeHtml(ctaLabel)}</a>`;

    bar.querySelector('[data-happy-app-banner="cta"]').addEventListener('click', (event) => {
        bar.remove();
        if (canOpen) {
            // A real tap — the browser allows the app hand-off here.
            event.preventDefault();
            trackBannerEvent('open', platform);
            onOpen();

            return;
        }
        trackBannerEvent('get', platform);
    });
    bar.querySelector('[data-happy-app-banner="dismiss"]').addEventListener('click', () => {
        bar.remove();
        trackBannerEvent('dismiss', platform);
        if (typeof onDismiss === 'function') {
            onDismiss();
        }
    });

    document.body.insertBefore(bar, document.body.firstChild);
    trackBannerEvent('view', platform);

    return bar;
};
